import { create } from "zustand";
import { stringify } from "qs";

import { executeAPIRequest } from "@/utils/api";
import {
  EpochRewardListItemType,
  EpochRewardsListApiResponseType,
  EpochRewardRecordApiType
} from "@/types";
import { normaliseEpochRewardsList } from "@/utils/epochRewardsList";

type FetchEpochRewardsOptions = {
  page?: number;
  limit?: number;
  status: string;
};

type UseEpochRewardsStoreProps = {
  fetchResults: (options: FetchEpochRewardsOptions) => Promise<{
    results: EpochRewardListItemType[];
    resultCount: number;
  }>;
  fetchEpochRewardsBySearchQuery: (options: { searchQuery?: string }) => Promise<
    {
      id: string;
      name: string;
      url: string;
    }[]
  >;
};

export const useEpochRewardsStore = create<UseEpochRewardsStoreProps>(() => ({
  fetchResults: async ({ page = 1, limit = 25, status }) => {
    const response = await executeAPIRequest<EpochRewardsListApiResponseType>({
      method: "get",
      url: `/io-explorer/tokenomics/rewards?${stringify({
        page,
        page_size: limit,
        status: status?.toLowerCase() === "all" ? undefined : status?.toLowerCase()
      })}`
    });

    const rewards = normaliseEpochRewardsList(response?.data?.rewards);
    const total = response?.data?.total;

    return {
      results: rewards,
      resultCount: typeof total === "number" && Number.isFinite(total) ? total : rewards.length
    };
  },
  fetchEpochRewardsBySearchQuery: async ({ searchQuery = "" }) => {
    const normalizedSearchQuery = searchQuery.trim();
    if (!normalizedSearchQuery) {
      return [];
    }

    try {
      const response = await executeAPIRequest<EpochRewardsListApiResponseType>({
        method: "get",
        url: `/io-explorer/tokenomics/rewards?${stringify({
          page: 1,
          page_size: 10,
          epoch_id: normalizedSearchQuery
        })}`
      });

      const rewards = normaliseEpochRewardsList(response?.data?.rewards);
      const records = Array.isArray(response?.data?.rewards) ? response.data.rewards : [];

      if (rewards.length > 0) {
        return rewards
          .filter((item) =>
            item.epochId.toLowerCase().includes(normalizedSearchQuery.toLowerCase())
          )
          .map((item) => ({
            id: item.id || item.epochId,
            name: item.epochId || "--",
            url: `/explorer/epochs/${item.epochId}`
          }));
      }

      return records
        .filter((record: EpochRewardRecordApiType) =>
          String(record?.epoch_id || "")
            .toLowerCase()
            .includes(normalizedSearchQuery.toLowerCase())
        )
        .map((record: EpochRewardRecordApiType) => {
          const epochId = String(record?.epoch_id || "");
          return {
            id: epochId || "unknown",
            name: epochId || "--",
            url: epochId ? `/explorer/epochs/${epochId}` : "/explorer/block-rewards"
          };
        });
    } catch (error) {
      return [];
    }
  }
}));
